import { useState, useEffect, useRef } from "react";
import { SessionProvider, useSessionContext } from "./contexts/SessionContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { CanvasProvider } from "./contexts/CanvasContext";
import { ToolProvider } from "./contexts/ToolContext";
import { SelectionProvider } from "./contexts/SelectionContext";
import { TokenProvider } from "./contexts/TokenContext";
import { ChatProvider } from "./contexts/ChatContext";
import { InitiativeProvider } from "./contexts/InitiativeContext";
import { SheetProvider } from "./contexts/SheetContext";
import { ControlledTokensSync } from './hooks/useControlledTokensSync';
import { SessionStoreSync } from './hooks/useSessionStoreSync';
import { StudioWorkspaceMemoryProvider } from './components/studio/studioWorkspaceMemory';
import { WorkspaceProvider } from "./contexts/WorkspaceContext";
import { LayoutProvider } from "./contexts/LayoutContext";
import { ShortcutProvider } from "./contexts/ShortcutContext";
import { AlertProvider } from "./contexts/AlertContext";
import { JournalProvider } from "./contexts/JournalContext";
import { QuestObjectivesProvider } from "./contexts/QuestObjectivesContext";
import { UndoProvider } from "./contexts/UndoContext";
import { CanvasInteractionProvider } from "./contexts/CanvasInteractionContext";
import StudioGate from "./components/studio/StudioGate";
import { SharedAiChatProvider } from "./hooks/useAiChat";
import { ImageGalleryProvider } from "./contexts/ImageGalleryContext";
import { ImageGalleryHost } from "./components/images/ImageGalleryHost";
import { ThemeSync } from "./components/ThemeSync";
import { ErrorBoundary } from "./components/ErrorBoundary";
import LoginForm from "./components/auth/LoginForm";
import RegisterForm from "./components/auth/RegisterForm";
import SessionListPanel from "./components/auth/SessionListPanel";
import ProfileSettings from "./components/auth/ProfileSettings";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Toaster } from "./components/ui/sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Dices, Sparkles } from "lucide-react";
import { useI18n, LanguageSwitcher } from "./i18n";
import { useReleaseNotes } from "./hooks/useReleaseNotes";
import { useBugReport } from "./hooks/useBugReport";
import type { GameSystemId } from "@roll1/shared";
import { GAME_SYSTEM_LABELS } from "./lib/gameSystem";
import BugReportDialog from "./components/BugReportDialog";
import { SplashCard } from "./components/SplashCard";
import { WhatsNewDialog } from "./components/WhatsNewDialog";
import { EditPrimitivesPreview } from "./components/character-hud/edit-primitives/__preview__";

export default function App() {
  // Dev-only short-circuit: `?primitive-preview` opens edit-primitives sandbox.
  // No SessionProvider needed — primitives are standalone.
  const previewMode =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('primitive-preview');

  // ThemeProvider sits above everything (incl. lobby/auth) so the whole app —
  // not just in-session screens — reflects the active theme. (Phase 2.)
  return (
    <ThemeProvider>
      {previewMode ? (
        <EditPrimitivesPreview />
      ) : (
        <SessionProvider>
          <AppRouter />
        </SessionProvider>
      )}
    </ThemeProvider>
  );
}

/** Routes between pre-session (auth/lobby) and in-session (provider tree + AppShell) */
function AppRouter() {
  const { t, locale } = useI18n();
  const { auth, mp, outgoingPageId } = useSessionContext();
  const [authMode, setAuthMode] = useState<"login" | "register">("login");
  const [joinMode, setJoinMode] = useState(false);
  const [joinCode, setJoinCode] = useState("");
  const [gameSystem, setGameSystem] = useState<GameSystemId>('dnd5e');
  const releaseNotes = useReleaseNotes();
  const bugReport = useBugReport(!mp.session);
  const [showSplash, setShowSplash] = useState(
    () => releaseNotes.hasUpdate && releaseNotes.currentHighlight != null,
  );

  // Handle ?join=CODE invite links — auto-join once authenticated
  const joinHandledRef = useRef(false);
  useEffect(() => {
    if (joinHandledRef.current || !auth.user || mp.session) return;
    const params = new URLSearchParams(window.location.search);
    const joinCode = params.get('join');
    if (joinCode) {
      joinHandledRef.current = true;
      mp.joinSession(joinCode.toUpperCase(), auth.user.displayName);
      // Clean URL without page reload
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [auth.user, mp.session, mp.joinSession]);

  // Bug report dialog — available on all screens (Ctrl+Alt+B / Ctrl+Shift+B)
  const bugReportDialog = (
    <BugReportDialog
      open={bugReport.dialogOpen}
      onOpenChange={bugReport.setDialogOpen}
      elementContext={bugReport.elementContext}
    />
  );

  // Auth loading or auto-rejoining session from sessionStorage (prevents lobby flash on F5)
  if (!mp.session) {
    if (auth.loading || mp.isRejoining) {
      return (
        <>
          <div className="lobby-panel">
            <p>{mp.isRejoining ? t("reconnect.message") : t("common.loading")}</p>
          </div>
          {bugReportDialog}
        </>
      );
    }

    // Not logged in — auth forms
    if (!auth.user) {
      return (
        <>
          <div className="lobby-panel">
            <div className="absolute top-4 right-4">
              <LanguageSwitcher />
            </div>
            <h1>
              <Dices size={24} className="inline-block mr-2 align-[-4px]" />
              Roll1 VTT
            </h1>
            {authMode === "login" ? (
              <LoginForm
                error={auth.error}
                onLogin={auth.login}
                onSwitchToRegister={() => setAuthMode("register")}
              />
            ) : (
              <RegisterForm
                error={auth.error}
                onRegister={auth.register}
                onSwitchToLogin={() => setAuthMode("login")}
              />
            )}
          </div>
          {bugReportDialog}
        </>
      );
    }

    // Logged in, no session — lobby
    return (
      <>
        <div className="lobby-panel">
          <h1>
            <Dices size={24} className="inline-block mr-2 align-[-4px]" />
            Roll1 VTT
          </h1>
          <div className="lobby-user-bar">
            <span className="lobby-user-greeting">{auth.user.displayName}</span>
            <button
              className="lobby-link-btn relative"
              onClick={() => releaseNotes.setShowWhatsNew(true)}
            >
              <Sparkles size={14} className="inline mr-1 align-[-2px]" />
              {t("releaseNotes.menuItem")}
              {releaseNotes.hasUpdate && (
                <span
                  className="badge-dot badge-dot--pulse"
                  aria-label={t("releaseNotes.badgeNew")}
                  role="status"
                />
              )}
            </button>
            <span className="lobby-link-dot">&middot;</span>
            <button className="lobby-link-btn" onClick={auth.logout}>
              {t("app.logout")}
            </button>
          </div>

          {mp.error && <div className="lobby-error">{mp.error}</div>}
          {mp.status === "connecting" && (
            <div className="lobby-status">{t("lobby.connecting")}</div>
          )}

          <Tabs defaultValue="sessions">
            <TabsList>
              <TabsTrigger value="sessions">{t("sessionPage.sessionsTab")}</TabsTrigger>
              <TabsTrigger value="profile">{t("sessionPage.profileTab")}</TabsTrigger>
            </TabsList>
            <TabsContent value="sessions">
              <SessionListPanel
                onJoinSession={mp.joinSession}
                userName={auth.user.displayName}
              />

              <div className="lobby-menu">
                <div className="flex items-center gap-2 w-full">
                  {process.env.NEXT_PUBLIC_FEATURE_MULTI_SYSTEM === 'true' && (
                    <Select value={gameSystem} onValueChange={(v) => setGameSystem(v as GameSystemId)}>
                      <SelectTrigger className="w-[160px]">
                        <SelectValue placeholder={t('gameSystem.label')} />
                      </SelectTrigger>
                      <SelectContent>
                        {(Object.entries(GAME_SYSTEM_LABELS) as [GameSystemId, string][]).map(([id, label]) => (
                          <SelectItem key={id} value={id}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <Button
                    onClick={() => mp.createSession(auth.user!.displayName, gameSystem)}
                    disabled={mp.status === "connecting"}
                  >
                    {t("lobby.createSession")}
                  </Button>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setJoinMode(true)}
                  disabled={mp.status === "connecting"}
                >
                  {t("app.joinByCode")}
                </Button>
              </div>

              {joinMode && (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (joinCode.trim()) {
                      mp.joinSession(
                        joinCode.trim().toUpperCase(),
                        auth.user!.displayName,
                      );
                      setJoinMode(false);
                      setJoinCode("");
                    }
                  }}
                  className="lobby-form"
                >
                  <label>
                    {t("lobby.sessionCode")}
                    <Input
                      value={joinCode}
                      onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                      maxLength={6}
                      placeholder="ABC123"
                      autoFocus
                      disabled={mp.status === "connecting"}
                    />
                  </label>
                  <div className="lobby-actions">
                    <Button
                      type="submit"
                      disabled={mp.status === "connecting" || !joinCode.trim()}
                    >
                      {t("lobby.enter")}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setJoinMode(false);
                        setJoinCode("");
                      }}
                    >
                      {t("common.cancel")}
                    </Button>
                  </div>
                </form>
              )}
            </TabsContent>
            <TabsContent value="profile">
              <ProfileSettings
                user={auth.user}
                error={auth.error}
                onUpdateProfile={auth.updateProfile}
              />
            </TabsContent>
          </Tabs>
        </div>
        {showSplash && releaseNotes.currentHighlight && (
          <SplashCard
            highlight={releaseNotes.currentHighlight[locale]}
            onDismiss={() => {
              setShowSplash(false);
              releaseNotes.markVersionSeen();
            }}
            onLearnMore={() => {
              setShowSplash(false);
              releaseNotes.markVersionSeen();
              releaseNotes.setShowWhatsNew(true);
            }}
          />
        )}
        <WhatsNewDialog
          open={releaseNotes.showWhatsNew}
          onOpenChange={(open) => {
            releaseNotes.setShowWhatsNew(open);
            if (!open) releaseNotes.markVersionSeen();
          }}
          versions={releaseNotes.versions}
          currentVersion={releaseNotes.currentVersion}
        />
        {bugReportDialog}
      </>
    );
  }

  // In session — provider tree + AppShell (splash + WhatsNew dialog handled inside AppShell)
  return (
    <ErrorBoundary key={mp.session?.id} onReset={() => mp.disconnect()}>
      <ThemeSync />
      <AlertProvider>
        <LayoutProvider>
        <WorkspaceProvider>
          <UndoProvider>
            <ToolProvider>
            <CanvasProvider>
            <CanvasInteractionProvider>
              <SelectionProvider outgoingPageId={outgoingPageId}>
              <TokenProvider>
                <ChatProvider>
                  <InitiativeProvider>
                    <SheetProvider>
                      <ControlledTokensSync />
                      <JournalProvider>
                        <SessionStoreSync />
                        <QuestObjectivesProvider>
                        <ShortcutProvider>
                        <StudioWorkspaceMemoryProvider>
                        <ImageGalleryProvider>
                        {/* One GM chat above the table ⇄ Studio swap: the conversation and a running answer survive it (31.10). */}
                        <SharedAiChatProvider>
                        <StudioGate />
                        </SharedAiChatProvider>
                        <ImageGalleryHost />
                        </ImageGalleryProvider>
                        </StudioWorkspaceMemoryProvider>
                        </ShortcutProvider>
                        </QuestObjectivesProvider>
                      </JournalProvider>
                    </SheetProvider>
                  </InitiativeProvider>
                </ChatProvider>
              </TokenProvider>
              </SelectionProvider>
            </CanvasInteractionProvider>
              <Toaster position="bottom-center" duration={4000} />
            </CanvasProvider>
            </ToolProvider>
          </UndoProvider>
        </WorkspaceProvider>
        </LayoutProvider>
      </AlertProvider>
    </ErrorBoundary>
  );
}
